// Minimal iframe-friendly converter. Same upload/convert/download flow as
// the main ToolPage, but stripped of navigation and marketing content, and
// always renders a small "Powered by" badge (the whole point of the embed).
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { UploadSimple, ArrowRight, CheckCircle, FileArrowDown, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = (process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function EmbedTool() {
  const { toolId } = useParams();
  const [tool, setTool] = useState(null);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Signal to embedders this is an embed
    document.documentElement.dataset.embed = 'true';
    document.body.style.background = '#F4F4F5';
    (async () => {
      try {
        const r = await axios.get(`${API}/tools/${toolId}`);
        setTool(r.data);
      } catch (_e) {
        setErr('Tool not found');
      }
    })();
  }, [toolId]);

  const addFiles = (incoming) => {
    if (!tool) return;
    const arr = Array.from(incoming);
    setFiles(tool.multi ? [...files, ...arr] : arr.slice(0, 1));
  };

  const convert = async () => {
    if (!files.length) return;
    setStatus('working');
    setErr('');
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    try {
      const r = await axios.post(`${API}/convert/${toolId}`, fd);
      setResult(r.data);
      setStatus('done');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Conversion failed');
      setStatus('error');
    }
  };

  if (err && !tool) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui' }}>
        <p style={{ fontWeight: 700 }}>Tool not found.</p>
        <PoweredBy />
      </div>
    );
  }
  if (!tool) {
    return <div style={{ padding: 24 }}><div style={{ height: 20, width: 160, background: '#e5e5e5' }} /></div>;
  }

  return (
    <div style={{ minHeight: '100vh', padding: 16, background: '#F4F4F5', fontFamily: 'IBM Plex Sans, system-ui' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: '#71717a', margin: 0 }}>
              Convert
            </p>
            <h1 data-testid="embed-tool-title" style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 0', color: '#0a0a0a', lineHeight: 1.1 }}>
              {tool.name}
            </h1>
          </div>
          <PoweredBy compact placement="header" />
        </div>

        {status === 'done' && result ? (
          <div data-testid="embed-success" style={{ marginTop: 20, padding: 20, background: '#fff', border: '2px solid #0a0a0a', boxShadow: '4px 4px 0 #0a0a0a', textAlign: 'center' }}>
            <CheckCircle weight="fill" size={40} color="#10b981" />
            <p style={{ fontWeight: 700, margin: '8px 0 4px', fontSize: 18 }}>Done!</p>
            <p style={{ fontSize: 13, color: '#52525b' }}>Size: {humanSize(result.size)}</p>
            <a
              data-testid="embed-download-btn"
              href={`${BACKEND_URL}${result.download_url}`}
              download={result.filename}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: '#0a0a0a', color: '#fff', padding: '10px 18px', fontWeight: 700, textDecoration: 'none', border: '2px solid #0a0a0a', boxShadow: '4px 4px 0 #0a0a0a' }}
            >
              <FileArrowDown weight="bold" size={16} /> Download
            </a>
            <div>
              <button
                onClick={() => { setResult(null); setFiles([]); setStatus('idle'); }}
                style={{ background: 'transparent', border: 'none', textDecoration: 'underline', marginTop: 12, fontSize: 13, cursor: 'pointer' }}
              >
                Convert another
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              data-testid="embed-drop-zone"
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
              style={{
                marginTop: 16,
                border: `3px ${dragOver ? 'solid' : 'dashed'} ${dragOver ? '#0a0a0a' : '#a1a1aa'}`,
                background: dragOver ? '#f4f4f5' : '#fff',
                padding: 28,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
            >
              <input
                ref={inputRef}
                data-testid="embed-file-input"
                type="file"
                accept={tool.accept}
                multiple={tool.multi}
                hidden
                onChange={(e) => addFiles(e.target.files)}
              />
              <UploadSimple weight="bold" size={28} />
              <p style={{ fontWeight: 700, marginTop: 8, fontSize: 15 }}>
                {dragOver ? 'Drop file(s)' : 'Drop files or click to browse'}
              </p>
              <p style={{ fontSize: 11, color: '#52525b', fontFamily: 'IBM Plex Mono, monospace', marginTop: 4 }}>
                {tool.accept}
              </p>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '2px solid #0a0a0a', padding: '8px 10px', marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                    <button
                      onClick={() => setFiles(files.filter((_, x) => x !== i))}
                      aria-label="Remove"
                      style={{ background: '#fff', border: '2px solid #0a0a0a', width: 26, height: 26, cursor: 'pointer' }}
                    >
                      <X weight="bold" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {err && (
              <div style={{ marginTop: 12, background: '#fee2e2', border: '2px solid #0a0a0a', padding: 10, fontSize: 13 }}>
                {err}
              </div>
            )}

            <button
              data-testid="embed-convert-btn"
              onClick={convert}
              disabled={!files.length || status === 'working'}
              style={{
                marginTop: 16,
                width: '100%',
                background: !files.length || status === 'working' ? '#a1a1aa' : '#0a0a0a',
                color: '#fff',
                padding: '12px 18px',
                fontWeight: 700,
                border: '2px solid #0a0a0a',
                boxShadow: '4px 4px 0 #0a0a0a',
                cursor: !files.length || status === 'working' ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'background 0.15s',
              }}
            >
              {status === 'working' ? 'Converting…' : (<><span>Convert</span><ArrowRight weight="bold" size={16} /></>)}
            </button>
          </>
        )}

        <PoweredBy placement="footer" />
      </div>
    </div>
  );
}

function PoweredBy({ compact, placement = 'footer' }) {
  return (
    <a
      data-testid={`embed-attribution-${placement}`}
      href={SITE}
      target="_blank"
      rel="noopener"
      style={{
        display: 'inline-block',
        marginTop: compact ? 0 : 16,
        fontSize: 11,
        fontFamily: 'IBM Plex Mono, monospace',
        color: '#71717a',
        textDecoration: 'none',
        letterSpacing: 1,
        textTransform: 'uppercase',
      }}
    >
      Powered by <span style={{ borderBottom: '2px solid #0a0a0a', color: '#0a0a0a', fontWeight: 700 }}>Go Convert Documents</span>
    </a>
  );
}
